// Analytics tracking utility
import { v4 as uuidv4 } from 'uuid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Categorize traffic source from referrer
const categorizeTrafficSource = (referrer) => {
  if (!referrer || referrer === '') {
    return { source: 'Direct', category: 'direct', platform: null };
  }

  const url = referrer.toLowerCase();

  // Search Engines
  const searchEngines = {
    'google': { name: 'Google', category: 'search' },
    'bing': { name: 'Bing', category: 'search' },
    'yahoo': { name: 'Yahoo', category: 'search' },
    'duckduckgo': { name: 'DuckDuckGo', category: 'search' },
    'yandex': { name: 'Yandex', category: 'search' },
    'baidu': { name: 'Baidu', category: 'search' },
    'ask.com': { name: 'Ask', category: 'search' },
    'aol': { name: 'AOL', category: 'search' },
    'ecosia': { name: 'Ecosia', category: 'search' }
  };

  // Social Media Platforms
  const socialMedia = {
    'facebook.com': { name: 'Facebook', category: 'social' },
    'fb.com': { name: 'Facebook', category: 'social' },
    'twitter.com': { name: 'Twitter/X', category: 'social' },
    'x.com': { name: 'Twitter/X', category: 'social' },
    't.co': { name: 'Twitter/X', category: 'social' },
    'instagram.com': { name: 'Instagram', category: 'social' },
    'linkedin.com': { name: 'LinkedIn', category: 'social' },
    'lnkd.in': { name: 'LinkedIn', category: 'social' },
    'tiktok.com': { name: 'TikTok', category: 'social' },
    'youtube.com': { name: 'YouTube', category: 'social' },
    'youtu.be': { name: 'YouTube', category: 'social' },
    'reddit.com': { name: 'Reddit', category: 'social' },
    'pinterest.com': { name: 'Pinterest', category: 'social' },
    'snapchat.com': { name: 'Snapchat', category: 'social' },
    'telegram': { name: 'Telegram', category: 'social' },
    't.me': { name: 'Telegram', category: 'social' },
    'whatsapp': { name: 'WhatsApp', category: 'social' },
    'discord': { name: 'Discord', category: 'social' },
    'vk.com': { name: 'VK', category: 'social' },
    'wechat': { name: 'WeChat', category: 'social' }
  };

  // Check search engines
  for (const [key, value] of Object.entries(searchEngines)) {
    if (url.includes(key)) {
      return { source: value.name, category: value.category, platform: key };
    }
  }

  // Check social media
  for (const [key, value] of Object.entries(socialMedia)) {
    if (url.includes(key)) {
      return { source: value.name, category: value.category, platform: key };
    }
  }

  // Other external sites
  try {
    const hostname = new URL(referrer).hostname;
    return { source: hostname, category: 'referral', platform: hostname };
  } catch {
    return { source: 'Unknown', category: 'unknown', platform: null };
  }
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
};

// Get geo location from IP (using free API)
const getGeoData = async () => {
  try {
    // Try to get real geo data from IP API
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      return {
        country: data.country_name || 'Unknown',
        city: data.city || 'Unknown'
      };
    }
  } catch (error) {
    console.log('Geo API error, using fallback');
  }
  
  // Fallback to browser language
  const lang = navigator.language || navigator.userLanguage;
  if (lang.includes('uk') || lang.includes('ua')) {
    return { country: 'Ukraine', city: 'Kyiv' };
  }
  return { country: 'Unknown', city: 'Unknown' };
};

// Track analytics event
export const trackEvent = async (eventType, eventName, metadata = {}) => {
  try {
    const geo = await getGeoData();
    const referrer = document.referrer || '';
    const trafficSource = categorizeTrafficSource(referrer);
    
    const event = {
      event_type: eventType,
      event_name: eventName,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      referrer: referrer,
      session_id: getSessionId(),
      metadata: {
        ...metadata,
        device_type: getDeviceType(),
        country: geo.country,
        city: geo.city,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        // Traffic source categorization
        traffic_source: trafficSource.source,
        traffic_category: trafficSource.category,
        traffic_platform: trafficSource.platform
      }
    };

    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event)
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track page view
export const trackPageView = () => {
  trackEvent('page_view', window.location.pathname);
};

// Track button click
export const trackButtonClick = (buttonName, additionalData = {}) => {
  trackEvent('button_click', buttonName, additionalData);
};

// Track scroll depth
let maxScrollDepth = 0;
export const initScrollTracking = () => {
  const trackScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;
      
      // Track milestones: 25%, 50%, 75%, 100%
      if ([25, 50, 75, 100].includes(scrollPercent)) {
        trackEvent('scroll', `scroll_${scrollPercent}`, { scroll_depth: scrollPercent });
      }
    }
  };

  window.addEventListener('scroll', trackScroll);
  return () => window.removeEventListener('scroll', trackScroll);
};

// Track time on page
let pageStartTime = Date.now();
export const trackTimeOnPage = () => {
  const duration = Math.round((Date.now() - pageStartTime) / 1000); // seconds
  trackEvent('time_on_page', window.location.pathname, { duration });
};

// Initialize analytics on page load
export const initAnalytics = () => {
  // Track page view
  trackPageView();
  
  // Track scroll
  const removeScrollTracking = initScrollTracking();
  
  // Track time on page when user leaves
  const handleBeforeUnload = () => {
    trackTimeOnPage();
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Cleanup function
  return () => {
    removeScrollTracking();
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};

// Track registration clicks (speaker vs participant)
export const trackRegistrationClick = (type) => {
  const eventName = type === 'speaker' ? 'register_speaker' : 'register_participant';
  trackButtonClick(eventName, { registration_type: type });
};

// Track CTA clicks
export const trackCTAClick = (ctaName, ctaLocation) => {
  trackButtonClick(`cta_${ctaName}`, { location: ctaLocation });
};
